import _ from "lodash";

// How the three key sections show what a key can reach — docs/scoped-keys.md
// §The auth app.
//
// A key is either organization-wide (any roles, `scope` null) or an AGENT key:
// `roles: ["agent"]`, bound to one resource of its organization by `scope`, and
// held to `rateLimit` by keys.check. An organization-wide key renders exactly as
// it always did; only an agent key gets the extra line and fields, so a list of
// ordinary keys does not grow columns that are empty on every row.

/** Whether a key is bound to one resource — the API's own test, `scope` set. */
export const isAgent = (key) => !!(key && key.scope);

/** `<service> <type> <id>`, in that order. */
export const describeScope = (scope) =>
  scope ? `${scope.service} ${scope.type} ${scope.id}` : null;

const count = (n) => Number(n).toLocaleString("en-US");

/** `60 / minute · 5,000 / day`. */
export const describeRateLimit = (rateLimit) =>
  rateLimit
    ? `${count(rateLimit.minute)} / minute · ${count(rateLimit.day)} / day`
    : null;

/** The one line an agent key carries under its id in a list. */
export const describeAccess = (key) => {
  if (!isAgent(key)) {
    return null;
  }

  const limit = describeRateLimit(key.rateLimit);
  return ["Agent key", describeScope(key.scope), limit].filter(Boolean).join(" · ");
};

/**
 * What an organization-wide create form sends: the fields it shows, and
 * nothing else.
 *
 * `/Public/Key/Create` also lists `scope`, and the form keeps every
 * property of its target in its values — so sending `values` whole would hand
 * the API whatever those hold. These forms mint organization-wide keys and name
 * their fields explicitly; an agent key is `agentInput`, from the organization
 * section's own form.
 */
export const createInput = (values, fields) => _.pick(values || {}, fields);

const DisplayOnly = (View) => ({ View, Edit: View });

/**
 * The row a list cell belongs to. A widget is handed only its own property's
 * value; `field.parent` is the object form-schema resolved, and its `parent` is
 * what ObjectComponent passed to getField, whose `values` is the row. That is
 * form-schema's shape rather than a documented prop, which is why
 * index.test.js renders a real row through it.
 */
const rowOf = (field) =>
  field && field.parent && field.parent.parent ? field.parent.parent.values : null;

/**
 * The `keyId` column of a key list: the id, and under it the agent line for an
 * agent key.
 */
export const keyId = DisplayOnly(({ value, field }) => {
  const access = describeAccess(rowOf(field));

  return (
    <>
      <span>{value}</span>
      {access && (
        <div className="small text-muted" data-testid="key-access">
          {access}
        </div>
      )}
    </>
  );
});

/** A key's `scope` on its view: `<service> <type> <id>`. */
export const keyScope = DisplayOnly(({ value }) => (
  <span data-testid="key-scope">{describeScope(value) || "Organization-wide"}</span>
));

/** A key's `rateLimit` on its view: `60 / minute · 5,000 / day`. */
export const keyRateLimit = DisplayOnly(({ value }) => (
  <span data-testid="key-rate-limit">{describeRateLimit(value) || "Not limited"}</span>
));

/** Every widget above, keyed by the name the configs below use. */
export const widgets = { keyId, keyScope, keyRateLimit };

/** A list's `keyId` column entry. */
export const keyIdColumn = { property: "keyId", widget: "keyId" };

/**
 * A key view's fields: `base` for every key, and the scope and limit after it
 * for an agent key only.
 */
export const viewFields = (key, base) =>
  isAgent(key)
    ? [
        ...base,
        { property: "scope", widget: "keyScope", label: "Bound to" },
        { property: "rateLimit", widget: "keyRateLimit" },
      ]
    : base;

const trim = (value) => (typeof value === "string" ? value.trim() : "");

/**
 * The `scope` argument for a filter: `{ service, type }` for every key of that
 * kind, with `id` for one resource's. Null — every key — until both service and
 * type are given; an empty id means every resource of the kind.
 */
export const scopeFilter = ({ service, type, id } = {}) => {
  const [s, t, i] = [trim(service), trim(type), trim(id)];
  if (!s || !t) {
    return null;
  }
  return i ? { service: s, type: t, id: i } : { service: s, type: t };
};

/**
 * What the agent key form sends: `roles: ["agent"]` and the one resource the
 * user named, which is the only bound shape auth-repoflow accepts. auth checks
 * the names' shape and nothing else — whether the resource exists and is this
 * organization's is the service's to answer when the key is used, so getting
 * them right is the user's. The description is trimmed and left out when empty.
 */
export const agentInput = ({ service, type, id, description } = {}) => {
  const scope = { service: trim(service), type: trim(type), id: trim(id) };
  if (!scope.service || !scope.type || !scope.id) {
    return null;
  }

  const input = { roles: ["agent"], scope };
  const text = trim(description);
  return text ? { ...input, description: text } : input;
};

/** Why a mint was refused, in words, for the refusal that is about the scope. */
export const describeCreateError = (error) => {
  const message = String((error && error.message) || error || "");
  if (message.includes("key.create.scope.invalid")) {
    return "An agent key needs a service, a type and an id, and the agent role only. Service and type are lowercase letters, digits and dashes, starting with a letter.";
  }
  return message;
};
